import React, { useCallback, useEffect, useRef } from 'react';
import { useFiniteSnapCarousel } from '../../hooks/useFiniteSnapCarousel';
import CarouselTouchDiagnostics from './CarouselTouchDiagnostics';

/** One slide. Memoized (like the WEEKLY FEATURED `WantedAdCard`) so the carousel's per-frame `activeIndex` scroll-state updates don't re-render the slide trees mid-fling. */
const SponsoredDateIdeaSlide = React.memo(function SponsoredDateIdeaSlide({
  idea,
  index,
  displayCount,
  registerCardRef,
}) {
  const photoUrl = idea.imageStorageUrl || idea.imageUrl;
  const isStripCenter = index === Math.floor(displayCount / 2);
  return (
    <article
      ref={(el) => registerCardRef(index, el)}
      className="fox-paid-dates-idea-slide"
      aria-roledescription="slide"
      aria-label={`${idea.place || 'Sponsored date idea'}, ${index + 1} of ${displayCount}`}
    >
      <div className="fox-paid-dates-idea-card-inner">
        <h4 className="fox-paid-dates-idea-title">{idea.place}</h4>
        {idea.location && (
          <p className="fox-paid-dates-idea-location">{idea.location}</p>
        )}
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="fox-paid-dates-idea-photo"
            loading={isStripCenter ? 'eager' : 'lazy'}
            fetchPriority={isStripCenter ? 'high' : 'auto'}
            decoding="async"
            draggable={false}
          />
        ) : (
          <div className="fox-paid-dates-idea-photo fox-paid-dates-idea-photo--placeholder" aria-hidden>
            📍
          </div>
        )}
        {idea.whatToGetOrDo && (
          <p className="fox-paid-dates-idea-what-to-do">{idea.whatToGetOrDo}</p>
        )}
        {idea.highlights && (
          <p className="fox-paid-dates-idea-highlights">{idea.highlights}</p>
        )}
      </div>
    </article>
  );
});

const PostedSponsoredDateIdeasCarousel = React.memo(function PostedSponsoredDateIdeasCarousel({
  dateIdeas,
  onCardClick,
  feedReady = false,
}) {
  const pointerRef = useRef(null);

  const {
    carouselRef,
    cardRefs,
    activeIndex,
    scrollToIndex,
    displayItems,
    displayCount,
  } = useFiniteSnapCarousel(dateIdeas, 5);
  const feedPending = !feedReady && displayCount === 0;
  const stripCenterIdx = Math.floor(displayCount / 2);

  /** Stable identity so `SponsoredDateIdeaSlide`'s memo isn't defeated by a fresh ref callback each render. */
  const registerCardRef = useCallback((idx, el) => {
    cardRefs.current[idx] = el;
  }, [cardRefs]);

  useEffect(() => {
    if (displayCount === 0) return undefined;
    const photoUrl = (idea) => idea?.imageStorageUrl || idea?.imageUrl || null;
    const centerUrl = photoUrl(displayItems[stripCenterIdx]);
    const rest = [];
    const seen = new Set();
    if (centerUrl) seen.add(centerUrl);
    displayItems.forEach((idea, i) => {
      if (i === stripCenterIdx) return;
      const url = photoUrl(idea);
      if (!url || seen.has(url)) return;
      seen.add(url);
      rest.push(url);
    });

    let cancelled = false;
    const loadOne = (url) => new Promise((resolve) => {
      if (!url || typeof url !== 'string') {
        resolve();
        return;
      }
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });

    loadOne(centerUrl).then(() => {
      if (cancelled) return;
      rest.forEach((url) => {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [displayItems, displayCount, stripCenterIdx]);

  const handleTrackPointerDown = (e) => {
    if (e.target.closest('.fox-paid-dates-ideas-dots')) return;
    pointerRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleTrackClick = (e) => {
    if (e.target.closest('.fox-paid-dates-ideas-dots')) return;
    const slideEl = e.target.closest('.fox-paid-dates-idea-slide');
    if (!slideEl) return;
    const start = pointerRef.current;
    if (start && (Math.abs(e.clientX - start.x) > 10 || Math.abs(e.clientY - start.y) > 10)) {
      return;
    }
    /* Side slide centers first; only the centered slide opens the popup. */
    const idx = cardRefs.current.indexOf(slideEl);
    if (idx !== -1 && idx !== activeIndex) {
      scrollToIndex(idx);
      return;
    }
    if (onCardClick) onCardClick();
  };

  if (displayCount === 0 && !feedPending) return null;

  return (
    <div
      className={`fox-paid-dates-ideas-carousel${feedPending ? ' fox-paid-dates-ideas-carousel--pending' : ''}`}
      aria-busy={feedPending}
    >
      <CarouselTouchDiagnostics trackRef={carouselRef} />
      <div
        ref={carouselRef}
        className={`fox-paid-dates-ideas-track${onCardClick && !feedPending ? ' fox-paid-dates-ideas-track--clickable' : ''}`}
        role="group"
        aria-roledescription="carousel"
        aria-label="Fox-Paid date ideas"
        onPointerDown={feedPending ? undefined : handleTrackPointerDown}
        onClick={feedPending ? undefined : handleTrackClick}
      >
        {feedPending ? (
          <article className="fox-paid-dates-idea-slide fox-paid-dates-idea-slide--pending" aria-hidden>
            <div className="fox-paid-dates-idea-card-inner">
              <h4 className="fox-paid-dates-idea-title">&nbsp;</h4>
              <p className="fox-paid-dates-idea-location">&nbsp;</p>
              <div className="fox-paid-dates-idea-photo fox-paid-dates-idea-photo--placeholder" />
              <p className="fox-paid-dates-idea-what-to-do">&nbsp;</p>
              <p className="fox-paid-dates-idea-highlights">&nbsp;</p>
            </div>
          </article>
        ) : (
          displayItems.map((idea, index) => (
            <SponsoredDateIdeaSlide
              key={idea.id}
              idea={idea}
              index={index}
              displayCount={displayCount}
              registerCardRef={registerCardRef}
            />
          ))
        )}
      </div>
      {(displayCount > 1 || feedPending) && (
        <div
          className="fox-paid-dates-ideas-dots"
          role="tablist"
          aria-label="Sponsored date ideas"
          aria-hidden={feedPending}
        >
          {feedPending ? (
            <span className="fox-paid-dates-idea-dot" />
          ) : (
            displayItems.map((idea, i) => (
              <button
                key={idea.id}
                type="button"
                role="tab"
                aria-selected={i === activeIndex}
                aria-label={`Go to date idea ${i + 1} of ${displayCount}`}
                className={`fox-paid-dates-idea-dot${i === activeIndex ? ' fox-paid-dates-idea-dot--active' : ''}`}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  scrollToIndex(i);
                }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
});

export default PostedSponsoredDateIdeasCarousel;
